import { useForm } from 'react-hook-form';
import TextInput from '../../../forms/TextInput';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { Stack, styled, Typography } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { InventoryManagementSettingFormRef } from './InventoryManagementDialog';
import { WeightedRandomization } from '../../../../data/models/inventory-service-configuration.model';

const PREFIX = 'WeightedRandomizationForm';

const classes = {
  formulaText: `${PREFIX}-formulaText`,
  coefficientTextInput: `${PREFIX}-coefficientTextInput`,
  coefficientTitle: `${PREFIX}-coefficientTitle`,
};

const StyledStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  marginTop: -22,

  [`& .${classes.formulaText}`]: {
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.coefficientTextInput}`]: {
    width: 123,
    marginTop: theme.spacing(1),
  },
  [`& .${classes.coefficientTitle}`]: {
    fontWeight: 600,
  },
}));

interface WeightedRandomizationFormProps {
  value?: WeightedRandomization;
  onValidityChange?: (valid: boolean) => void;
  isReadOnly?: boolean;
}

export const WeightedRandomizationForm = forwardRef<
  InventoryManagementSettingFormRef,
  WeightedRandomizationFormProps
>(({ value, onValidityChange, isReadOnly }, ref) => {
  const t = useCommonTranslation();
  const {
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      weightedRandomization: {
        priorityFactorCoefficient: 0,
        scoreFactorCoefficient: 0,
        capacityFactorCoefficient: 0,
      },
    },
  });
  const requiredFieldMessage = t('common.requiredField');

  useImperativeHandle(ref, () => ({
    getValues,
    triggerValidation: () => trigger(),
    isValid,
  }));

  useEffect(() => {
    if (value != null) {
      setValue('weightedRandomization', value);
    }
  }, [value]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const weightedRandomizationsKeys: Array<keyof WeightedRandomization> = [
    'priorityFactorCoefficient',
    'scoreFactorCoefficient',
    'capacityFactorCoefficient',
  ];
  const priorityFactorCoefficient = watch(
    'weightedRandomization.priorityFactorCoefficient',
  );
  const scoreFactorCoefficient = watch(
    'weightedRandomization.scoreFactorCoefficient',
  );
  const capacityFactorCoefficient = watch(
    'weightedRandomization.capacityFactorCoefficient',
  );
  return (
    <StyledStack>
      <Typography className={classes.formulaText}>
        {`W = ${priorityFactorCoefficient}p + ${scoreFactorCoefficient}s + ${capacityFactorCoefficient}c`}
      </Typography>
      {weightedRandomizationsKeys.map((key) => (
        <Stack>
          <Typography className={classes.coefficientTitle}>
            {t(`inventoryManagement.${key}`)}
          </Typography>
          <Typography>{t(`inventoryManagement.${key}Description`)}</Typography>
          <TextInput
            className={classes.coefficientTextInput}
            name={`weightedRandomization.${key}`}
            label=""
            type="number"
            inputProps={{
              min: 0,
              max: 10,
            }}
            readOnly={isReadOnly}
            control={control}
            required
            rules={{
              required: requiredFieldMessage,
              min: {
                value: 0,
                message: t('inventoryManagement.rangeError'),
              },
              max: {
                value: 10,
                message: t('inventoryManagement.rangeError'),
              },
            }}
          />
        </Stack>
      ))}
    </StyledStack>
  );
});
